'use client';

import { useCallback, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { SELECTED_BORDER, SELECTED_ON } from './selected';

/**
 * The pill toggle: the ONE control for a small switch in place. A choice of
 * two to five, side by side: the chart's measure and period, the events
 * list's Upcoming and Past, "Athletes pay it on top" or "You absorb it".
 *
 * Not for switching between whole sections of a page: that is the underline
 * tab bar (FvdTabBar). The rule (CEO, 24 Oct): sections are tabs, small
 * switches are this.
 *
 * The chosen option is filled in the brand's dark green (--fvd-selected-border,
 * the same green that outlines a selected card) with --fvd-selected-on for its
 * label: 9.3:1 in light, 4.8:1 in dark, where the green is the calmer one, not
 * the bright accent. The others are quiet ink on the pill's hairline.
 *
 * A radio group by default: it sets a value, and the arrow keys (and Home,
 * End) move the choice as they do in any radio group. `semantics="tabs"` makes
 * it a tab list instead, for a switch that shows one of several panels in
 * place (FvdTabs still draws through here for the screens not yet moved).
 *
 * On a phone every option is at least 44px tall. With `fill` (the default) the
 * options share a phone's width equally; without it the pill keeps its own
 * width and scrolls sideways when it runs out of room.
 */
export interface FvdSegmentedOption<V extends string> {
  value: V;
  label: string;
  /** A small icon before the label (decorative: the label says it). */
  icon?: ReactNode;
  /** A count beside the label, in mono. 'pending' holds its place while it loads. */
  count?: number | null | 'pending';
  /** Shown in the count's place: a figure that is not a count (money taken). */
  display?: string;
  /** The count is work waiting (amber while the option is not chosen). */
  attention?: boolean;
}

/**
 * Where a key moves the choice: the arrows step and wrap (right and down
 * forward, left and up back), Home and End jump to the ends. From no choice
 * (current -1) forward lands on the first and back on the last. null: the key
 * is not one of these, so the browser keeps it.
 */
export function segmentKeyTarget(key: string, current: number, length: number): number | null {
  if (length <= 0) return null;
  if (key === 'Home') return 0;
  if (key === 'End') return length - 1;
  const step = key === 'ArrowRight' || key === 'ArrowDown' ? 1 : key === 'ArrowLeft' || key === 'ArrowUp' ? -1 : 0;
  if (!step) return null;
  if (current < 0) return step > 0 ? 0 : length - 1;
  return (current + step + length) % length;
}

export function FvdSegmented<V extends string>({
  options,
  value,
  onChange,
  label,
  disabled = false,
  fill = true,
  semantics = 'radio',
  showZero = false,
  minHeight = 36,
  style,
}: {
  options: Array<FvdSegmentedOption<V>>;
  /** null: nothing chosen (the value is set elsewhere, say a dropdown beside it). */
  value: V | null;
  onChange: (value: V) => void;
  /** For screen readers: what is being chosen. */
  label: string;
  disabled?: boolean;
  /**
   * On a phone, share the width equally (true, the default), or keep its own
   * width and scroll (false). 'always' shares the width at every size (a
   * form's full-width band choice).
   */
  fill?: boolean | 'always';
  /** A radio group (sets a value, the default) or a tab list (shows a panel). */
  semantics?: 'radio' | 'tabs';
  /** Show a count of 0 (a list filter: "Drafts 0"); otherwise only counts above 0 show. */
  showZero?: boolean;
  /**
   * Each option's height off a phone: 36 by default, the floor CLAUDE.md
   * sets. A dense row may opt down (never below 28) by asking. A phone is
   * always 44.
   */
  minHeight?: number;
  style?: CSSProperties;
}) {
  const isMobile = useIsMobile();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  // A value that matches nothing shows the first as chosen; null shows none.
  const current = value === null ? -1 : Math.max(0, options.findIndex((o) => o.value === value));
  const tabs = semantics === 'tabs';
  const share = fill === 'always' || (isMobile && fill === true);
  const bar = useRef<HTMLDivElement>(null);

  // Keeping its own width, the pill scrolls when it runs out of room, as the
  // tab bar does: the edge with more options past it fades, and the chosen
  // option is kept in view (sideways only, so the page does not move).
  const [more, setMore] = useState({ left: false, right: false });
  const measure = useCallback(() => {
    const box = bar.current;
    if (!box || share) return;
    const left = box.scrollLeft > 2;
    const right = box.scrollLeft + box.clientWidth < box.scrollWidth - 2;
    setMore((m) => (m.left === left && m.right === right ? m : { left, right }));
  }, [share]);
  useEffect(() => {
    measure();
    const box = bar.current;
    if (!box || share || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, [measure, share, options.length]);
  useEffect(() => {
    const box = bar.current;
    const el = current >= 0 ? refs.current[current] : null;
    if (!box || !el || share) return;
    const left = el.offsetLeft - box.offsetLeft;
    if (left < box.scrollLeft) box.scrollLeft = Math.max(0, left - 8);
    else if (left + el.offsetWidth > box.scrollLeft + box.clientWidth) box.scrollLeft = left + el.offsetWidth - box.clientWidth + 8;
    measure();
  }, [current, measure, share]);
  const FADE = 24;
  const mask = !share && (more.left || more.right)
    ? `linear-gradient(to right, ${more.left ? 'transparent' : '#000'} 0, #000 ${more.left ? FADE : 0}px, #000 calc(100% - ${more.right ? FADE : 0}px), ${more.right ? 'transparent' : '#000'} 100%)`
    : undefined;

  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const next = segmentKeyTarget(e.key, current, options.length);
    if (next === null) return;
    e.preventDefault();
    onChange(options[next].value);
    const el = refs.current[next];
    el?.focus();
  };

  return (
    <div
      ref={bar}
      role={tabs ? 'tablist' : 'radiogroup'}
      aria-label={label}
      onScroll={share ? undefined : measure}
      style={{
        display: 'flex', gap: 2, padding: 3, boxSizing: 'border-box',
        border: '1px solid var(--fvd-hairline-strong)', borderRadius: 'var(--fvd-radius-pill)',
        width: share ? '100%' : 'fit-content', maxWidth: '100%',
        // A pill too wide for its box scrolls as one control, rather than
        // wrapping its own outline round two ragged lines.
        overflowX: share ? undefined : 'auto', scrollbarWidth: 'none',
        maskImage: mask, WebkitMaskImage: mask,
        ...style,
      }}
    >
      {options.map((o, i) => {
        const on = i === current;
        const count = o.count;
        const figure = o.display ?? (typeof count === 'number' && (count > 0 || showZero) ? count.toLocaleString('en-GB') : null);
        // Work waiting is said on the option itself (a label on a bare span is
        // not read out), and only while it is not the one chosen.
        const waiting = o.attention && !on && typeof count === 'number' && count > 0 ? `${o.label}, ${figure} waiting` : undefined;
        return (
          <button
            key={o.value}
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role={tabs ? 'tab' : 'radio'}
            aria-checked={tabs ? undefined : on}
            aria-selected={tabs ? on : undefined}
            // Only the chosen option is in the Tab order (the first, when none is).
            tabIndex={on || (current < 0 && i === 0) ? 0 : -1}
            aria-label={waiting}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            onKeyDown={onKey}
            className="fvd-pill-option"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              // Sharing the width, each takes its label's room first and the
              // rest is split, so "3 months" is not squeezed onto two lines
              // to give "Year" the same width.
              flex: share ? '1 1 auto' : 'none', minWidth: 0,
              border: 'none', fontFamily: 'inherit', fontSize: 13, padding: share ? '0 6px' : isMobile ? '0 8px' : '0 12px',
              minHeight: isMobile ? 44 : Math.max(28, minHeight), borderRadius: 'var(--fvd-radius-pill)',
              whiteSpace: share ? 'normal' : 'nowrap', lineHeight: 1.25, textAlign: 'center',
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled && !on ? 0.6 : 1,
              background: on ? SELECTED_BORDER : 'transparent',
              color: on ? SELECTED_ON : 'var(--fvd-ink-secondary)',
              fontWeight: on ? 550 : 450,
            }}
          >
            {o.icon ? <span aria-hidden style={{ display: 'inline-flex', flex: 'none' }}>{o.icon}</span> : null}
            {o.label}
            {count === 'pending' ? (
              <span aria-hidden style={{ display: 'inline-block', width: 12, height: 9, borderRadius: 4, background: on ? 'currentColor' : 'var(--fvd-hairline)', opacity: on ? 0.35 : 1 }} />
            ) : figure !== null ? (
              <span
                style={{
                  fontFamily: 'var(--fvd-font-mono)', fontSize: 11.5, fontWeight: 450,
                  // On the chosen option, the label's own colour, solid: faded,
                  // it fell under 4.5:1 on the dark-mode green.
                  color: on ? SELECTED_ON : o.attention ? 'var(--fvd-amber-text)' : 'var(--fvd-ink-muted)',
                }}
              >
                {figure}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
